import React, { useCallback } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NumberField, TextField, CardList } from '@travelity/form';
import { number, object, string } from 'yup';
import {
    AgeBands,
    CapacityItem,
    capacityItemToCapacityDto,
    CapacityOptionTypes,
    getCapacityDtoToCapacity,
    useCreateCapacity,
} from '@travelity/api';
import { Tag } from '@travelity/ui';
import { useFormContext } from 'react-hook-form';
import WarningIcon from '@mui/icons-material/Warning';
import { CapacityModal } from '../capacity-modal';
import {
    OnboardingClasses,
    OnboardingTours,
    useOnboardingTour,
} from '../../onboarding';
import { Onboarding } from '../../onboarding/onboarding.component';

export enum FlatFields {
    MIN = 'min',
    MAX = 'max',
}

const schema = object({
    name: string().required('Name is a required field').uniqName(),
}).when('.type', ([type], sch) => {
    if (type === CapacityOptionTypes.FLAT) {
        return sch.shape({
            limit: object()
                .shape({
                    min: number().min(1).required('Min is required'),
                    max: number().when(['min'], ([min], s) => {
                        return min
                            ? s
                                  .min(
                                      min + 1,
                                      'Maximum should be more then minimum'
                                  )
                                  .required()
                            : s;
                    }),
                })
                .required(),
        });
    }
    return sch.shape({
        limit: object().when('.', ([limit], s) => {
            return Object.values(limit).find(v => v)
                ? s
                : s.shape({
                      infants: number()
                          .required('At least one ageband count required')
                          .min(1, 'At least one ageband count required'),
                  });
        }),
    });
});

export interface ProductCapacityFormProps {
    isUpdate?: boolean;
    preview?: boolean;
    showFull?: boolean;
    showErrors?: boolean;
}

const ProductCapacityForm: React.FC<ProductCapacityFormProps> = ({
    isUpdate,
    preview,
    showFull,
    showErrors,
}) => {
    const { t } = useTranslation('common');
    const { watch, setValue, trigger } = useFormContext();
    const { onboardingProps: capacitiesOnboardingProps } = useOnboardingTour(
        OnboardingTours.CAPACITY
    );

    const [showBrowse, setShowBrowse] = React.useState(false);
    const browse = useCallback(() => setShowBrowse(true), []);

    const addCapacities = useCallback(
        (capacities: CapacityItem[]) => {
            const currentCapacities = watch('capacities') || [];
            setValue('capacities', [...currentCapacities, ...capacities]);
            trigger('capacities');
            setShowBrowse(false);
        },
        [watch, setValue, trigger]
    );

    const { mutateAsync: createCapacity } = useCreateCapacity();

    const onCapacityAdd = useCallback(async (capacity: Record<string, any>) => {
        const { items: [newCapacity] = [] } = await createCapacity(
            capacityItemToCapacityDto(capacity as CapacityItem)
        );
        return getCapacityDtoToCapacity(newCapacity);
    }, []);

    return (
        <>
            <Stack
                spacing={2}
                sx={{ width: { xs: '100%', sm: '453px' } }}
                className={OnboardingClasses.CAPACITY_CARDS}
            >
                {isUpdate && !preview && (
                    <>
                        <Alert
                            severity="warning"
                            icon={
                                <WarningIcon
                                    sx={{
                                        color: '#EC8031',
                                        fontSize: '24px',
                                    }}
                                />
                            }
                            sx={{
                                alignItems: 'center',
                                color: '#2B395B',
                                mt: 0,
                                '.MuiAlert-action': { pt: 0 },
                            }}
                        >
                            Some capacity options may be dependencies on pricing
                            options
                        </Alert>

                        <Alert
                            severity="warning"
                            icon={
                                <WarningIcon
                                    sx={{
                                        color: '#EC8031',
                                        fontSize: '24px',
                                    }}
                                />
                            }
                            sx={{
                                alignItems: 'center',
                                color: '#2B395B',
                                mt: 1,
                                '.MuiAlert-action': { pt: 0 },
                            }}
                        >
                            Removing a capacity option will result in automatic
                            removal of the dependent pricing option
                        </Alert>
                        <Alert
                            severity="warning"
                            icon={
                                <WarningIcon
                                    sx={{
                                        color: '#EC8031',
                                        fontSize: '24px',
                                    }}
                                />
                            }
                            sx={{
                                alignItems: 'center',
                                color: '#2B395B',
                                mt: 1,
                                '.MuiAlert-action': { pt: 0 },
                            }}
                        >
                            Make sure to verify pricing options after updating
                            the list of capacity options
                        </Alert>
                    </>
                )}
                <CardList
                    showErrors={showErrors}
                    disabled={preview}
                    formParams={{ schema }}
                    addClassName={OnboardingClasses.ADD_CAPACITY_OPTION}
                    actionButtonsClassName={
                        OnboardingClasses.CAPACITY_CONTROL_PANEL
                    }
                    maxHeight={showFull ? undefined : 500}
                    renderHeader={({ editing, item }) =>
                        editing ? (
                            <TextField
                                sx={{ mx: '10px' }}
                                autoFocus
                                InputProps={{
                                    sx: { fontSize: '14px', pl: 1.5 },
                                }}
                                className={OnboardingClasses.CAPACITY_NAME}
                                placeholder="Type Name"
                                name="name"
                                variant="standard"
                                showErrors
                                helperText=""
                                fullWidth
                            />
                        ) : (
                            <Box
                                component="span"
                                title={item.name}
                                sx={{
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 2,
                                }}
                            >
                                {item.name}
                            </Box>
                        )
                    }
                    renderContent={({ item, editing }) =>
                        editing ? (
                            <Stack gap={0.5} p={2}>
                                {Object.values(
                                    item.type === CapacityOptionTypes.FLAT
                                        ? FlatFields
                                        : AgeBands
                                ).map(name => (
                                    <Stack direction="row" alignItems="center">
                                        <Box
                                            sx={{
                                                width: '80px',
                                                color: '#949BAC',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {t(name)}
                                        </Box>
                                        <Box sx={{ flexGrow: 2 }}>
                                            <NumberField
                                                name={`limit.${name}`}
                                                showErrors
                                                label=""
                                                size="small"
                                                fullWidth
                                            />
                                        </Box>
                                    </Stack>
                                ))}
                            </Stack>
                        ) : (
                            <Stack
                                direction="row"
                                gap={1}
                                px={2}
                                py={1}
                                flexWrap="wrap"
                            >
                                {item.type === CapacityOptionTypes.FLAT
                                    ? Object.values(FlatFields).map(
                                          name =>
                                              !!item.limit[name] && (
                                                  <Tag
                                                      label={t(name)}
                                                      values={[
                                                          item.limit[name],
                                                      ]}
                                                  />
                                              )
                                      )
                                    : Object.values(AgeBands).map(
                                          name =>
                                              !!item.limit[name] && (
                                                  <Tag
                                                      label={t(name)}
                                                      values={[
                                                          item.limit[name],
                                                      ]}
                                                  />
                                              )
                                      )}
                            </Stack>
                        )
                    }
                    addButtonText="Add Capacity Option"
                    onItemSave={onCapacityAdd}
                    itemOptions={[
                        {
                            label: 'Flat Count',
                            type: CapacityOptionTypes.FLAT,
                            min: 0,
                            max: 0,
                        },
                        {
                            label: 'Ageband Count',
                            type: CapacityOptionTypes.AGE_BAND,
                        },
                        {
                            label: 'Browse',
                            onClick: browse,
                        },
                    ]}
                    disableItemEdit={() => true}
                    name="capacities"
                />
            </Stack>

            {showBrowse && (
                <CapacityModal
                    handleCancel={() => setShowBrowse(false)}
                    handleConfirm={addCapacities}
                    open={showBrowse}
                />
            )}
            {!preview && !isUpdate && (
                <Onboarding {...capacitiesOnboardingProps} />
            )}
        </>
    );
};

export default React.memo(ProductCapacityForm);
